import { createSelector } from "@reduxjs/toolkit";

import i18n from "../../config/i18n";
import { logger } from "../../utils/logger";

import type { RootState } from "../index";
import type { UserProfile } from "@lingoshade/api";

// Basic selectors
export const selectAuth = (state: RootState): RootState["auth"] => state.auth;
export const selectUser = (state: RootState): RootState["auth"]["user"] =>
  state.auth.user;
export const selectIsAuthenticated = (state: RootState): boolean =>
  state.auth.isAuthenticated;
export const selectAuthLoading = (state: RootState): boolean =>
  state.auth.loading;
export const selectAuthError = (state: RootState): string | null =>
  state.auth.error;
export const selectToken = (state: RootState): string | null =>
  state.auth.token;
export const selectAvatarUrl = (state: RootState): string | null =>
  state.auth.avatarUrl;

// Registration selectors
// Deprecated legacy selector kept for backward compatibility – always returns null
export const selectRegistrationStep = (_state: RootState): number | null =>
  null;
export const selectRegistrationData = (
  state: RootState,
): RootState["auth"]["registrationData"] => state.auth.registrationData;

// Memoized selectors
export const selectUserRole = createSelector(
  [selectUser],
  (user: UserProfile | null): string | undefined => user?.role,
);

export const selectIsAdmin = createSelector(
  [selectUserRole],
  (role: string | undefined): boolean => role === "ADMIN",
);

// Note: UserProfile doesn't have subscriptionStatus field, this selector always returns undefined
export const selectSubscriptionStatus = createSelector(
  [selectUser],
  (_user: UserProfile | null): string | undefined => undefined,
);

export const selectHasActiveSubscription = createSelector(
  [selectSubscriptionStatus],
  (status: string | undefined): boolean =>
    status === "ACTIVE" || status === "CANCELLED_AT_PERIOD_END",
);

export const selectIsEmailVerified = createSelector(
  [selectUser],
  (user: UserProfile | null): boolean => user?.emailVerified ?? false,
);

export const selectUserInfo = createSelector(
  [selectUser],
  (
    user: UserProfile | null,
  ): {
    id: string;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    fullName: string | null;
    role: string | null;
    emailVerified: boolean;
    nativeLanguage: string | null;
    targetLanguage: string | null;
    level: string | null;
  } | null =>
    user
      ? {
          id: user.id,
          email: user.email ?? null,
          firstName: user.firstName ?? null,
          lastName: user.lastName ?? null,
          fullName:
            user.firstName && user.lastName
              ? `${user.firstName} ${user.lastName}`
              : (user.email ?? null),
          role: user.role ?? null,
          emailVerified: user.emailVerified ?? false,
          nativeLanguage: user.nativeLanguage ?? null,
          targetLanguage: user.targetLanguage ?? null,
          level: user.level ?? null,
        }
      : null,
);

export const selectRegistrationComplete = createSelector(
  [selectRegistrationStep, selectRegistrationData],
  (step: number | null, data: null): boolean => step === null && data === null,
);

// Note: registrationData is always null (deprecated), but selector kept for backward compatibility
export const selectCanProceedToStep2 = createSelector(
  [selectRegistrationStep, selectRegistrationData],
  (step: number | null, _data: null): boolean => step === 2,
);

// Anonymous user selectors
export const selectIsAnonymous = (state: RootState): boolean =>
  state.auth.isAnonymous;

export const selectAnonymousSession = (
  state: RootState,
): RootState["auth"]["anonymousSession"] => state.auth.anonymousSession;

// Combined selectors
export const selectHasAnyAuth = createSelector(
  [selectIsAuthenticated, selectIsAnonymous],
  (isAuthenticated: boolean, isAnonymous: boolean): boolean =>
    isAuthenticated || isAnonymous,
);

export const selectEffectiveLanguages = createSelector(
  [selectUser, selectAnonymousSession],
  (
    user: UserProfile | null,
    anonymousSession: {
      sessionId?: string;
      targetLanguage?: string;
      nativeLanguage?: string;
      level?: string;
    } | null,
  ): {
    nativeLanguage: string | null;
    targetLanguage: string | null;
  } => ({
    nativeLanguage:
      user?.nativeLanguage ?? anonymousSession?.nativeLanguage ?? null,
    targetLanguage:
      user?.targetLanguage ?? anonymousSession?.targetLanguage ?? null,
  }),
);

// Effective user info that works for both authenticated and anonymous users
export const selectEffectiveUserInfo = createSelector(
  [selectUser, selectAnonymousSession, selectIsAnonymous],
  (
    user: UserProfile | null,
    anonymousSession: {
      sessionId?: string;
      targetLanguage?: string;
      nativeLanguage?: string;
      level?: string;
    } | null,
    isAnonymous: boolean,
  ): {
    id: string | null;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    fullName: string;
    role: string | null;
    emailVerified: boolean;
    nativeLanguage: string | null;
    targetLanguage: string | null;
    level: string | null;
  } => {
    // Debug logging in development
    logger.debug("[selectEffectiveUserInfo] Input values:", {
      user: !!user,
      userId: user?.id,
      isAnonymous,
      anonymousSession: !!anonymousSession,
      anonymousTargetLang: anonymousSession?.targetLanguage,
      anonymousNativeLang: anonymousSession?.nativeLanguage,
    });

    if (user) {
      // Authenticated user
      const result = {
        id: user.id,
        email: user.email ?? null,
        firstName: user.firstName ?? null,
        lastName: user.lastName ?? null,
        fullName:
          user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : (user.email ?? "User"),
        role: user.role ?? null,
        emailVerified: user.emailVerified ?? false,
        nativeLanguage: user.nativeLanguage ?? null,
        targetLanguage: user.targetLanguage ?? null,
        level: user.level ?? null,
      };

      logger.debug(
        "[selectEffectiveUserInfo] Returning authenticated user:",
        result,
      );

      return result;
    } else if (isAnonymous && anonymousSession) {
      // Anonymous user
      const result = {
        id: null,
        email: null,
        firstName: null,
        lastName: null,
        fullName: i18n.t("common:interface.anonymousUser"),
        role: null,
        emailVerified: false,
        nativeLanguage: anonymousSession.nativeLanguage ?? null,
        targetLanguage: anonymousSession.targetLanguage ?? null,
        level: anonymousSession.level ?? null,
      };

      logger.debug(
        "[selectEffectiveUserInfo] Returning anonymous user:",
        result,
      );

      return result;
    } else {
      // No user data available
      const result = {
        id: null,
        email: null,
        firstName: null,
        lastName: null,
        fullName: "Guest",
        role: null,
        emailVerified: false,
        nativeLanguage: null,
        targetLanguage: null,
        level: null,
      };

      logger.debug(
        "[selectEffectiveUserInfo] Returning guest (no data):",
        result,
      );

      return result;
    }
  },
);
